import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Trophy,
  Star,
  Clock,
  Gamepad2,
  TrendingUp,
  Shield,
  Award,
  Calendar,
  Target,
  Share2,
  ExternalLink,
  Copy,
  ArrowRight,
  Archive,
  ShoppingCart
} from 'lucide-react';
import { MissionsPanel } from '@/components/MissionsPanel';
import {
  getPlayerStats,
  type PlayerStats
} from '@/services/pointsService';
import { getPlayerFavorites } from '@/services/likesService';
import { getPlayerPurchasedKeys, type PurchasedKey } from '@/services/purchasedKeysService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getPlaytimeHistory as getTrustPlaytimeHistory } from '@/services/trustScoreService';
import { PLAYER_LEVELS, BADGES, BADGE_TIERS } from '@/data/missions';
import {
  getUserSteamGames,
  formatPlaytime,
  getSteamIconUrl,
  type SteamPlaytimeGame
} from '@/services/steamPlaytimeService';
import { updatePlaytimeTracking } from '@/services/playtimeTrackingService';
import { shouldSyncSteamData, syncSteamDataForPlayer, getSteamStatsFromDB } from '@/services/steamSyncService';
import { recalculatePlayerBadges } from '@/services/pointsService';
import { supabase } from '@/integrations/supabase/client';
import { Gems } from '@/components/Gems';
import { slugify } from '@/lib/slugify';
import { getXpForLevel } from '@/data/leveling';
import { LevelProgressBar } from '@/components/LevelProgressBar';

export default function PlayerProfile() {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const [playtimeHistory, setPlaytimeHistory] = useState<any[]>([]);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [purchasedKeys, setPurchasedKeys] = useState<PurchasedKey[]>([]);
  const [steamGames, setSteamGames] = useState<SteamPlaytimeGame[]>([]);
  const [myDevelopedGames, setMyDevelopedGames] = useState<any[]>([]);
  const [purchasedGamesPlaytime, setPurchasedGamesPlaytime] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let channel: any;

    if (user?.id) {
      loadPlayerData();

      // Listen for real-time profile changes
      channel = supabase
        .channel(`player_profile_changes_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${user.id}`,
          },
          () => {
            // Re-fetch all player data when profile is updated
            loadPlayerData();
          }
        )
        .subscribe();
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user?.id]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value }, { replace: true });
  };

  const loadPlayerData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [stats, history, favs, keys] = await Promise.all([
        getPlayerStats(user.id),
        getTrustPlaytimeHistory(user.id),
        getPlayerFavorites(user.id),
        getPlayerPurchasedKeys(user.id)
      ]);

      // Charger les jeux Steam de manière asynchrone (ne bloque pas le chargement)
      getUserSteamGames(user.id).then(games => {
        setSteamGames(games);
      }).catch(() => {
        // Ignorer les erreurs Steam, ne pas bloquer l'affichage
        setSteamGames([]);
      });

      // Si l'utilisateur est un joueur et qu'il est aussi développeur de jeux, afficher ses jeux développés
      if (userType === 'player') {
        const { data: developedGames } = await supabase
          .from('games')
          .select('id, title, cover_image_url, steam_url')
          .eq('developer_id', user.id);

        setMyDevelopedGames(developedGames || []);
      }

      // Vérifier si une synchronisation Steam est nécessaire (une fois par jour)
      const needsSync = await shouldSyncSteamData(user.id);

      // Si besoin de sync, lancer en arrière-plan (ne pas bloquer l'affichage)
      if (needsSync) {
        syncSteamDataForPlayer(user.id).catch(error => {
          console.error('Erreur lors de la synchronisation Steam:', error);
        });
      }

      // Récupérer les stats Steam depuis la BDD (rapide)
      const steamStats = await getSteamStatsFromDB(user.id);

      // Créer un map pour accéder rapidement aux stats par game_id
      const statsMap = new Map(
        steamStats.map(stat => [stat.game_id, stat])
      );

      // Construire la liste des jeux achetés avec playtime depuis la BDD
      const purchasedGamesWithPlaytime = [];
      const gamesForTracking = [];
      let gamesCompleted100 = 0;

      for (const purchase of keys) {
        const gameStats = statsMap.get(purchase.game_id);

        // Utiliser les stats de la BDD si disponibles
        if (gameStats) {
          // Utiliser les achievements depuis la BDD (pas de calcul dynamique)
          let achievementsStats = null;
          if (gameStats.total_achievements > 0) {
            // Utiliser le pourcentage stocké en BDD si disponible, sinon le calculer
            const percentage = (gameStats as any).achievement_percentage !== undefined
              ? (gameStats as any).achievement_percentage
              : Math.round((gameStats.unlocked_achievements / gameStats.total_achievements) * 100);

            achievementsStats = {
              total: gameStats.total_achievements,
              unlocked: gameStats.unlocked_achievements,
              percentage: percentage
            };

            // Vérifier si le jeu est complété à 100%
            // Un jeu est considéré complété si : 100% achievements ET au moins 2h de jeu (120 minutes)
            if (percentage === 100 && gameStats.playtime_forever >= 120) {
              gamesCompleted100++;
            }
          }

          purchasedGamesWithPlaytime.push({
            ...purchase,
            playtime: gameStats.playtime_forever,
            playtime_2weeks: gameStats.playtime_2weeks,
            achievements: achievementsStats // Directement depuis la BDD
          });

          // Ajouter aux jeux pour le tracking
          gamesForTracking.push({
            game_id: purchase.game_id,
            steam_game_id: gameStats.steam_game_id,
            playtime: gameStats.playtime_forever,
            playtime_2weeks: gameStats.playtime_2weeks
          });
        } else {
          // Si pas de stats dans la BDD, ajouter quand même le jeu sans playtime/achievements
          purchasedGamesWithPlaytime.push({
            ...purchase,
            playtime: 0,
            playtime_2weeks: 0,
            achievements: null
          });
        }
      }

      setPurchasedGamesPlaytime(purchasedGamesWithPlaytime);

      // Mettre à jour la table playtime_tracking
      let finalStats = stats;
      if (gamesForTracking.length > 0) {
        await updatePlaytimeTracking(user.id, gamesForTracking);

        // Recharger les stats après la mise à jour (les badges seront recalculés automatiquement par updatePlaytimeTracking)
        // Passer gamesCompleted100 pour les stats, mais ne pas forcer le recalcul (les badges sont déjà mis à jour)
        const updatedStats = await getPlayerStats(user.id, gamesCompleted100, false);
        if (updatedStats) {
          finalStats = updatedStats;
        }
      }

      // Si on a des jeux complétés à 100%, recalculer les badges (affecte games_completed_100)
      if (gamesCompleted100 > 0) {
        recalculatePlayerBadges(user.id, gamesCompleted100).catch(error => {
          console.error('Error recalculating badges for completed games:', error);
        });
      }

      setPlayerStats(finalStats);
      setPlaytimeHistory(history);
      setFavorites(favs);
      setPurchasedKeys(keys);
    } catch (error) {
      // console.error('Error loading player data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShareProfile = async () => {
    if (!user) return;

    // Fetch display name from profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('id', user.id)
      .single();

    if (profile?.display_name) {
      const slug = slugify(profile.display_name);
      const publicProfileUrl = `${window.location.origin}/p/${slug}`;

      try {
        await navigator.clipboard.writeText(publicProfileUrl);
        toast.success('Link copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy link');
      }
    }
  };

  const handleGoToPublicProfile = async () => {
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('id', user.id)
      .single();

    if (profile?.display_name) {
      const slug = slugify(profile.display_name);
      navigate(`/p/${slug}`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!playerStats) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">Unable to load your statistics.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  const levelInfo = PLAYER_LEVELS[playerStats.level as keyof typeof PLAYER_LEVELS];
  const nextLevel = getNextLevel(playerStats.level);
  const progressToNextLevel = getProgressToNextLevel(playerStats.trust_score, playerStats.level);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">My Player Profile</h1>
              <p className="text-muted-foreground">
                Manage your Gems, missions and track your progress
              </p>
            </div>
            <div className="flex items-center gap-1 bg-card p-1 rounded-lg">
              <Button
                variant="outline"
                onClick={handleGoToPublicProfile}
                className="flex items-center gap-2 hover:bg-muted border-0 py-1 px-2 h-8 hover:text-primary text-xs"
              >
                Public Profile
                <ArrowRight className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleShareProfile}
                title="Copy profile link"
                className="hover:bg-muted !bg-card shrink-0 border-0 py-1 px-2 h-8 hover:text-primary"
              >
                <Copy className="!w-3 !h-3" />
              </Button>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 gap-1 h-auto p-1">
            <TabsTrigger value="overview" className="h-10">Overview</TabsTrigger>
            <TabsTrigger value="missions" className="h-10">Missions</TabsTrigger>
            <TabsTrigger value="playtime" className="h-10">Play Time</TabsTrigger>
            <TabsTrigger value="favorites" className="h-10">Favorites</TabsTrigger>
            <TabsTrigger value="purchased" className="h-10">Purchased</TabsTrigger>
            <TabsTrigger value="achievements" className="h-10">Badges</TabsTrigger>
          </TabsList>


          {/* Player Level & XP Progress */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 w-full">
            <div className="flex-1 w-full">
              <LevelProgressBar
                xp={playerStats.xp}
                playerLevel={playerStats.player_level}
                small={false}
              />
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Button onClick={() => window.location.href = "/inventory"} variant="outline" size="sm" className="gap-2">
                <Archive className="w-4 h-4" />
                Inventory
              </Button>
              <Button onClick={() => window.location.href = "/shop"} variant="default" size="sm" className="gap-2 bg-yellow-500 hover:bg-yellow-600 text-black">
                <ShoppingCart className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <TabsContent value="overview" className="space-y-6">
            {/* Stats principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Level card commented out for players */}
              {/* <Card className="p-6">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: levelInfo?.color }}
                  >
                    {playerStats.level.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Level</p>
                    <p className="text-xl font-bold">{levelInfo?.name}</p>
                  </div>
                </div>
              </Card> */}

              <Card className="p-6">
                <div className="flex items-center gap-3">
                  <Trophy className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Gems</p>
                    <p className="text-xl font-bold">{playerStats.coins} <Gems with_text={false} /></p>
                  </div>
                </div>
              </Card>

              {/* Trust Score card commented out for players */}
              {/* <Card className="p-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Trust Score</p>
                    <p className="text-xl font-bold">{playerStats.trust_score}/100</p>
                  </div>
                </div>
              </Card> */}

              <Card className="p-6">
                <div className="flex items-center gap-3">
                  <Gamepad2 className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Games Tested</p>
                    <p className="text-xl font-bold">{playerStats.games_tested}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Playtime</p>
                    <p className="text-xl font-bold">{Math.round(playerStats.total_playtime / 60)}h</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Progress to next level commented out for players */}
            {/* {nextLevel && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Progress to {nextLevel.name}</h3>
                  <Badge variant="outline">{progressToNextLevel}%</Badge>
                </div>
                <Progress value={progressToNextLevel} className="h-3 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {nextLevel.min_trust_score - playerStats.trust_score} trust points remaining
                </p>
              </Card>
            )} */}

            {/* Badges */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Earned Badges</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {playerStats.badges.map((badgeName, index) => {
                  // Find the badge in BADGE_TIERS
                  let badgeInfo = null;
                  for (const category of Object.values(BADGE_TIERS)) {
                    const found = category.find((tier: any) => tier.name === badgeName);
                    if (found) {
                      badgeInfo = found;
                      break;
                    }
                  }

                  return (
                    <div key={index} className="p-3 border rounded-custom-paddings bg-muted/30 border-border/30">
                      <div className="flex items-center sm:items-start gap-2 sm:gap-3">
                        <div className="text-xl sm:text-2xl flex-shrink-0">{badgeInfo?.icon}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-foreground/80 break-words">{badgeInfo?.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 sm:line-clamp-2">{badgeInfo?.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {playerStats.badges.length === 0 && (
                  <p className="col-span-full text-center text-muted-foreground py-8">
                    No badges earned yet
                  </p>
                )}
              </div>
            </Card>

            {/* Detailed statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Gaming Progress</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Games Purchased</span>
                    <span className="font-medium">{purchasedKeys.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Games Tested</span>
                    <span className="font-medium">{playerStats.games_tested}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Completion Rate</span>
                    <span className="font-medium">
                      {(() => {
                        const purchasedGames = purchasedKeys.length || 1;
                        return Math.round((playerStats.games_tested / purchasedGames) * 100);
                      })()}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Achievements Progress</span>
                    <span className="font-medium">{
                      (() => {
                        if (!purchasedGamesPlaytime || purchasedGamesPlaytime.length === 0) return '0%';
                        let total = 0, unlocked = 0;
                        purchasedGamesPlaytime.forEach((game) => {
                          if (game.achievements?.total > 0) {
                            total += game.achievements.total;
                            unlocked += game.achievements.unlocked;
                          }
                        });
                        return total === 0 ? '0%' : `${Math.round((unlocked / total) * 100)}%`;
                      })()
                    }</span>
                  </div>
                  {(() => {
                    let gamesCompleted100 = 0;
                    purchasedGamesPlaytime.forEach((game) => {
                      if (game.achievements?.percentage === 100 && game.playtime >= 120) {
                        gamesCompleted100++;
                      }
                    });
                    return gamesCompleted100 > 0 ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Games 100% Completed</span>
                        <span className="font-medium">{gamesCompleted100}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Community & Activity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Likes Given</span>
                    <span className="font-medium">{playerStats.likes_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Missions done</span>
                    <span className="font-medium">{playerStats.social_actions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Playtime</span>
                    <span className="font-medium">{Math.round(playerStats.total_playtime / 60)}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Member Since</span>
                    <span className="font-medium">{playerStats.days_since_signup} days ago</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Badges Earned</span>
                    <span className="font-medium">{playerStats.badges.length}</span>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="missions">
            <MissionsPanel playerId={user?.id || ''} />
          </TabsContent>

          <TabsContent value="playtime">
            <div className="space-y-6">
              {/* Mes jeux développés */}
              {myDevelopedGames.length > 0 && (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">My Developed Games</h3>
                  <div className="space-y-3">
                    {myDevelopedGames.map((game) => (
                      <div
                        key={game.id}
                        className="flex items-center justify-between p-4 border rounded-custom-paddings hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/game/${game.id}`)}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          {game.cover_image_url ? (
                            <img
                              src={game.cover_image_url}
                              alt={game.title}
                              className="w-12 h-12 rounded object-cover"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                              <Gamepad2 className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{game.title}</p>
                            <p className="text-sm text-muted-foreground">
                              Developed game
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary">Developer</Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Jeux achetés avec playtime */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {myDevelopedGames.length > 0 ? 'My Purchased Games' : 'Play Time History'}
                </h3>
                {purchasedGamesPlaytime.length > 0 ? (
                  <div className="space-y-3">
                    {purchasedGamesPlaytime.map((purchase) => (
                      <div
                        key={purchase.id}
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-custom-paddings hover:bg-muted/50 transition-colors cursor-pointer gap-3 md:gap-0"
                        onClick={() => navigate(`/game/${purchase.game_id}`)}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          {purchase.game?.cover_image_url ? (
                            <img
                              src={purchase.game.cover_image_url}
                              alt={purchase.game.title}
                              className="w-32 h-16 rounded-custom-paddings p-0 object-cover"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder.svg';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                              <Gamepad2 className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{purchase.game?.title}</p>
                            <p className="text-[12px] text-muted-foreground">
                              Purchased {new Date(purchase.purchased_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:ml-4 border-black/20 rounded-custom-paddings bg-border px-perso-20">
                          <div className="text-left md:text-right">
                            <p className="font-semibold text-lg">{formatPlaytime(purchase.playtime)}</p>
                            <p className="text-xs text-muted-foreground">total playtime</p>
                          </div>
                          {purchase.playtime_2weeks > 0 && (
                            <div className="text-left md:text-right border-t md:border-t-0 border-black/20 md:border-l pt-3 md:pt-0 md:pl-4">
                              <p className="font-semibold text-lg">{formatPlaytime(purchase.playtime_2weeks)}</p>
                              <p className="text-xs text-muted-foreground">last 2 weeks</p>
                            </div>
                          )}
                          {purchase.achievements && (
                            <div className="text-left md:text-right border-t md:border-t-0 border-black/20 md:border-l pt-3 md:pt-0 md:pl-4">
                              <p className="font-semibold text-lg">
                                {purchase.achievements.unlocked}/{purchase.achievements.total}
                              </p>
                              <p className="text-xs text-muted-foreground">achievements</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-2">
                      No playtime available
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Purchase games to track your playtime
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="favorites">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Favorite Games</h3>
              {favorites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favorites.map((favorite) => (
                    <div
                      key={favorite.id}
                      className="border rounded-custom-paddings p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/game/${favorite.game_id}`)}
                    >
                      <div className="flex items-center gap-3">
                        {favorite.game?.cover_image_url && (
                          <img
                            src={favorite.game.cover_image_url}
                            alt={favorite.game.title}
                            className="w-32 h-16 rounded-custom-paddings p-0 object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{favorite.game?.title}</p>
                          <p className="text-[12px] text-muted-foreground">
                            by {favorite.game?.developer?.display_name || favorite.game?.developer?.studio_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Added {new Date(favorite.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No favorite games yet
                </p>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="purchased">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Purchased Games</h3>
              {purchasedKeys.length > 0 ? (
                <div className="space-y-4">
                  {purchasedKeys.map((purchase) => (
                    <div
                      key={purchase.id}
                      className="border rounded-custom-paddings p-4 w-full"
                    >
                      <div className="flex items-start gap-4 flex-col md:flex-row">
                        {/* Game Image - Clickable */}
                        <div
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => navigate(`/game/${purchase.game_id}`)}
                        >
                          {purchase.game?.cover_image_url && (
                            <img
                              src={purchase.game.cover_image_url}
                              alt={purchase.game.title}
                              className="w-48 h-32 ml-1 mt-1 rounded-custom-paddings p-0 object-cover"
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Game Title - Clickable */}
                          <h4
                            className="font-medium text-lg cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate(`/game/${purchase.game_id}`)}
                          >
                            {purchase.game?.title}
                          </h4>

                          <p className="text-xs text-muted-foreground mb-3">
                            Purchased for {purchase.coins_spent} Gems • {new Date(purchase.purchased_at).toLocaleDateString()}
                          </p>

                          {/* Key Section */}
                          {purchase.key?.key_code ? (
                            <div className="mt-3 p-3 bg-muted rounded-custom-paddings w-fit border group">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <p className="text-xs text-muted-foreground">Your Key:</p>
                                  <p className="font-mono text-sm font-medium break-all blur-sm group-hover:blur-none transition-all duration-200 ml-2 mr-4">
                                    {purchase.key.key_code}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => {
                                    navigator.clipboard.writeText(purchase.key.key_code);
                                    toast.success('Key copied to clipboard!');
                                  }}
                                >
                                  Copy
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 p-3 bg-orange-50 rounded border border-orange-200">
                              <p className="text-xs text-orange-600">
                                Key not available (purchased before system update)
                              </p>
                            </div>
                          )}

                          {/* Steam Link */}
                          {purchase.game?.steam_url && (
                            <a
                              href={purchase.game.steam_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block text-xs text-blue-600 hover:underline mt-2 text-center md:text-left"
                            >
                              View on Steam
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No games purchased yet
                </p>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="achievements">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                Badges ({playerStats.badges.length}/{(() => {
                  let total = 0;
                  Object.values(BADGE_TIERS).forEach((tiers) => {
                    total += tiers.length;
                  });
                  return total;
                })()})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(Object.entries(BADGE_TIERS) as unknown as [string, { name: string; description: string; icon: string; requirement: any }[]][]).map(([key, tiers]) => {
                  const earnedTiers = tiers.filter(tier => playerStats.badges.includes(tier.name));

                  return tiers.map((tier, index) => {
                    const isEarned = playerStats.badges.includes(tier.name);

                    return (
                      <div
                        key={`${key}-${index}`}
                        className={`p-3 rounded-custom-paddings transition-all ${isEarned
                          ? 'bg-green-500/5'
                          : 'bg-muted/30'
                          }`}
                      >
                        <div className="flex h-full items-center px-2 gap-3">
                          <div className="text-2xl flex-shrink-0">{tier.icon}</div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm ${isEarned ? 'text-green-600 dark:text-green-400' : 'text-foreground/80'
                              }`}>
                              {tier.name}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {tier.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

// Fonctions utilitaires
function getNextLevel(currentLevel: string): typeof PLAYER_LEVELS[keyof typeof PLAYER_LEVELS] | null {
  const levels = Object.entries(PLAYER_LEVELS);
  const currentIndex = levels.findIndex(([key]) => key === currentLevel);

  if (currentIndex < levels.length - 1) {
    return levels[currentIndex + 1][1];
  }

  return null;
}

function getProgressToNextLevel(trustScore: number, currentLevel: string): number {
  const nextLevel = getNextLevel(currentLevel);
  if (!nextLevel) return 100;

  const currentLevelInfo = PLAYER_LEVELS[currentLevel as keyof typeof PLAYER_LEVELS];
  const range = nextLevel.min_trust_score - currentLevelInfo.min_trust_score;
  const progress = trustScore - currentLevelInfo.min_trust_score;

  return Math.max(0, Math.min(100, (progress / range) * 100));
}
